import type {
	ChecklistItemSnapshot,
	ChecklistMode,
	ReviewVerdict,
	ValidationAssetStatus,
	WorkOrderKind,
	WorkOrderSortKey,
} from '@atlas/types'
import type { SortDirection } from '@atlas/ui-components'
import { normalizeContext } from '@/domain/resolved-context.normalize'
import type { ResolvedContext } from '@/domain/resolved-context.types'
import type {
	BoardWorkflow,
	CreateWorkOrderInput,
	DeliverableFile,
	DeliverableReview,
	RawWorkOrderDetail,
	UpdateWorkOrderInput,
	WorkOrder,
	WorkOrderChild,
	WorkOrderDetail,
} from '@/domain/work-order.types'
import type { ResolvedWorkflowSnapshot, ValidationLane, WorkflowPhase } from '@/domain/workflow.types'
import { apiClient } from './api.client'

/**
 * Work-order API client — mirrors the work-order-service REST shape, so the MSW
 * mock and the live API are interchangeable. Envelope convention ({ data })
 * matches the other services.
 */
const unwrap = <T>(data: { data: T }): T => data.data

export interface WorkOrderListParams {
	status?: string
	source?: string
	/** Text within the order code, title, external id or Workfront number. */
	q?: string
	/** Only orders the caller is assigned to or is the PM of. */
	mine?: boolean
	/** Only orders of this level: the factory briefs, or the asset briefs. */
	kind?: WorkOrderKind
	/** Orders whose current status is configured with this phase, in any workflow. */
	'filters[phase]'?: WorkflowPhase
	/** Comma-separated status codes; matches any. */
	'filters[status]'?: string
	/** Comma-separated brand value codes; matches any, products included. */
	'filters[brand]'?: string
	/** Comma-separated user ids; matches any assignee. */
	'filters[assignee]'?: string
	sort?: WorkOrderSortKey
	order?: SortDirection
	page?: number
	limit?: number
}

/** A selectable status for a list filter: one per code across the tenant's workflows. */
export interface WorkOrderStatusOption {
	code: string
	label: string
}

/**
 * A board page: the work orders plus the workflows they belong to, with each
 * workflow's active statuses. Both travel in one response so the board renders
 * configured columns without a second request and without touching any
 * workflow-administration endpoint.
 */
export interface WorkOrderBoardPage {
	workOrders: WorkOrder[]
	workflows: BoardWorkflow[]
	/** Orders matching the filters across every page, not only the ones returned. */
	total: number
	totalPages: number
}

/**
 * A single work order plus the workflow it is assigned to, with that workflow's
 * active statuses. The configuration travels with the order — not with a board
 * page — so a detail surface can validate a transition target without the order
 * appearing in any paginated list. `null` when the order has no workflow, or
 * its workflow is missing, inactive, soft-deleted, or another tenant's.
 */
export interface WorkOrderDetailRead {
	workOrder: WorkOrder
	workflow: BoardWorkflow | null
	/** Legal transitions + guards + lane for the state timeline/action bar (design.md D7). Same null contract as `workflow`. */
	workflowSnapshot: ResolvedWorkflowSnapshot | null
}

export const workOrderApi = {
	statuses: (): Promise<WorkOrderStatusOption[]> =>
		apiClient.get<{ data: WorkOrderStatusOption[] }>('/work-orders/statuses').then((r) => unwrap(r.data)),
	list: (params?: WorkOrderListParams): Promise<WorkOrderBoardPage> =>
		apiClient
			.get<{
				data: WorkOrder[]
				workflows?: BoardWorkflow[]
				pagination: { total: number; totalPages: number }
			}>('/work-orders', { params })
			.then((r) => ({
				workOrders: r.data.data,
				workflows: r.data.workflows ?? [],
				total: r.data.pagination.total,
				totalPages: r.data.pagination.totalPages,
			})),
	get: (id: string): Promise<WorkOrderDetailRead> =>
		apiClient
			.get<{ data: WorkOrder; workflow?: BoardWorkflow | null; workflowSnapshot?: ResolvedWorkflowSnapshot | null }>(
				`/work-orders/${id}`,
			)
			.then((r) => ({
				workOrder: r.data.data,
				workflow: r.data.workflow ?? null,
				workflowSnapshot: r.data.workflowSnapshot ?? null,
			})),
	create: (body: CreateWorkOrderInput) =>
		apiClient.post<{ data: WorkOrder }>('/work-orders', body).then((r) => unwrap(r.data)),
	update: (id: string, patch: UpdateWorkOrderInput) =>
		apiClient.patch<{ data: WorkOrder }>(`/work-orders/${id}`, patch).then((r) => unwrap(r.data)),
	transition: (id: string, toStatus: string) =>
		apiClient.post<{ data: WorkOrder }>(`/work-orders/${id}/transitions`, { toStatus }).then((r) => unwrap(r.data)),
	archive: (id: string) => apiClient.delete<void>(`/work-orders/${id}`).then((r) => r.data),
	/** A parent's live children with their status, descriptors and costs. */
	children: (id: string): Promise<WorkOrderChild[]> =>
		apiClient.get<{ data: WorkOrderChild[] }>(`/work-orders/${id}/children`).then((r) => unwrap(r.data)),
	// Normalizes both the live thin shape (bare refs) and the mock's rich shape
	// into the panel's ResolvedContext (see resolved-context.normalize).
	context: (id: string): Promise<ResolvedContext> =>
		apiClient.get<{ data: unknown }>(`/work-orders/${id}/context`).then((r) => normalizeContext(unwrap(r.data))),
	/**
	 * The detail page's single read: order + hierarchy + context + deliverables
	 * grouped by `deliverable_type`, each file carrying a presigned thumbnail URL.
	 * Replaces five per-type discover calls, a thumbnail call per image card, and
	 * the whole-board fetch the page used to resolve parent/children.
	 */
	detail: (id: string): Promise<WorkOrderDetail> =>
		apiClient
			.get<{ data: RawWorkOrderDetail; workflowSnapshot?: ResolvedWorkflowSnapshot | null }>(
				`/work-orders/${id}/detail`,
			)
			.then((r) => {
				const data = unwrap(r.data)
				return {
					...data,
					context: normalizeContext(data.context),
					workflowSnapshot: r.data.workflowSnapshot ?? null,
				}
			}),
	// Mark or un-mark a final visual on the order's current review stage. The
	// stage is the server's to resolve; the response is the recorded mark.
	reviewDeliverable: (id: string, fileId: string, verdict: ReviewVerdict) =>
		apiClient
			.put<{ data: DeliverableReview }>(`/work-orders/${id}/deliverables/${fileId}/review`, { verdict })
			.then((r) => unwrap(r.data)),
	// Record a produced file attached to the order → emits work_order.file_attached
	// so the upload shows in the order's activity feed (D10).
	recordFileAttached: (id: string, fileId: string) =>
		apiClient.post<{ data: unknown }>(`/work-orders/${id}/files`, { fileId }).then((r) => r.data),
	// Typed download-package manifest for `work` | `distribution`. Each file arrives
	// DESCRIBED — name, content type, size, tags, presigned thumbnail — because the package
	// is reviewed and composed from before it is produced, and a bare object ref cannot be
	// shown to anyone. The frontend signs + downloads each file it was asked for.
	// (`validation` is generated on demand, not packaged.)
	packageManifest: (id: string, type: PackageType) =>
		apiClient.get<{ data: PackageManifest }>(`/work-orders/${id}/packages/${type}`).then((r) => unwrap(r.data)),
	// Excel import → classified messages. The `.xlsx` is uploaded
	// first through the presigned-S3 file flow; this endpoint takes only the
	// resulting `fileId` (NOT the bytes) and fetches the object server-side. The
	// server validates (row/card match, required copy) and creates one message per
	// copy field, returning the created count + ids.
	importMessages: (id: string, fileId: string, languageIso?: string) =>
		apiClient
			.post<{ data: ImportMessagesResult }>(`/work-orders/${id}/messages/import`, { fileId, languageIso })
			.then((r) => unwrap(r.data)),
	// Request a validation asset (verification PDF + card mockups) — gated server-side
	// on ≥1 final-visual deliverable AND ≥1 message. Returns the created request row.
	requestValidationAsset: (id: string, body: ValidationAssetRequestInput) =>
		apiClient
			.post<{ data: ValidationAssetRequest }>(`/work-orders/${id}/validation-asset-requests`, body)
			.then((r) => unwrap(r.data)),
	// List the order's validation-asset requests (newest first) — each carries its
	// render status and, when `ready`, a `resultManifest.outputs` of produced outputs.
	listValidationAssetRequests: (id: string) =>
		apiClient
			.get<{ data: ValidationAssetRequest[] }>(`/work-orders/${id}/validation-asset-requests`)
			.then((r) => unwrap(r.data)),
	// Delete a validation-asset request — soft-deletes the request row AND the
	// produced files it references (they stop surfacing in classification search).
	deleteValidationAssetRequest: (id: string, requestId: string) =>
		apiClient.delete<void>(`/work-orders/${id}/validation-asset-requests/${requestId}`).then((r) => r.data),
	// What the order's artwork is generated from — null until someone configures it,
	// which the section renders as an empty form rather than an error.
	generationContext: (id: string): Promise<GenerationContext | null> =>
		apiClient.get<{ data: GenerationContext | null }>(`/work-orders/${id}/generation-context`).then((r) => r.data.data),
	// Records the prompt, its exact version, the values and the text the author saw.
	// The rendered text is sent rather than re-derived server-side: the row exists to
	// record what was approved.
	saveGenerationContext: (id: string, body: GenerationContextInput): Promise<GenerationContext> =>
		apiClient
			.put<{ data: GenerationContext }>(`/work-orders/${id}/generation-context`, body)
			.then((r) => unwrap(r.data)),
	// Get-or-create the active checklist instance for a transition attempt: static
	// resolves (creating on first call) or returns the same instance on later calls
	// within the same attempt; dynamic returns the latest authored instance, or
	// null if none has been authored yet. Null also when the transition carries no
	// `checklist_complete` hook at all.
	resolveChecklistInstance: (workOrderId: string, transitionId: string): Promise<ChecklistInstance | null> =>
		apiClient
			.post<{ data: ChecklistInstance | null }>(`/work-orders/${workOrderId}/transitions/${transitionId}/checklist`)
			.then((r) => unwrap(r.data)),
	toggleChecklistInstanceItem: (instanceId: string, itemId: string, checked: boolean): Promise<ChecklistInstance> =>
		apiClient
			.patch<{ data: ChecklistInstance }>(`/checklist-instances/${instanceId}/items/${itemId}`, { checked })
			.then((r) => unwrap(r.data)),
	// Confirm-only — there is no "unconfirm" endpoint; unchecking a required item
	// is how a static instance's confirmation clears itself server-side.
	confirmChecklistInstance: (instanceId: string): Promise<ChecklistInstance> =>
		apiClient
			.post<{ data: ChecklistInstance }>(`/checklist-instances/${instanceId}/confirm`)
			.then((r) => unwrap(r.data)),
	// Every live change request on the order, oldest first, resolved ones included.
	listChangeRequests: (workOrderId: string): Promise<ChangeRequestDTO[]> =>
		apiClient
			.get<{ data: ChangeRequestDTO[] }>(`/work-orders/${workOrderId}/change-requests`)
			.then((r) => unwrap(r.data)),
	// Admin only — raised on the order's current status.
	createChangeRequest: (workOrderId: string, itemLabels: string[]): Promise<ChangeRequestDTO> =>
		apiClient
			.post<{ data: ChangeRequestDTO }>(`/work-orders/${workOrderId}/change-requests`, { itemLabels })
			.then((r) => unwrap(r.data)),
	verifyChangeRequestItem: (changeRequestId: string, itemId: string, verified: boolean): Promise<ChecklistInstance> =>
		apiClient
			.patch<{ data: ChecklistInstance }>(`/change-requests/${changeRequestId}/items/${itemId}`, { verified })
			.then((r) => unwrap(r.data)),
	removeChangeRequestItem: (changeRequestId: string, itemId: string): Promise<ChecklistInstance> =>
		apiClient
			.delete<{ data: ChecklistInstance }>(`/change-requests/${changeRequestId}/items/${itemId}`)
			.then((r) => unwrap(r.data)),
	deleteChangeRequest: (changeRequestId: string): Promise<void> =>
		apiClient.delete<void>(`/change-requests/${changeRequestId}`).then(() => undefined),
}

/** A change request as the API returns it: its dynamic checklist instance plus the status it was raised on, that status's lane, and the status it sends the order back to. */
export interface ChangeRequestDTO extends ChecklistInstance {
	raisedOnStatus: string
	lane: ValidationLane | null
	sentBackToStatus: string
}

/** A `work_order_checklist` instance, as the transition gate reads it — dates as ISO strings, mirroring `IWorkOrderChecklistInstance`'s DTO. */
export interface ChecklistInstance {
	instanceId: string
	tenantId: number
	workOrderId: string
	transitionId: string
	mode: ChecklistMode
	templateId: string | null
	items: ChecklistItemSnapshot[]
	confirmed: boolean
	confirmedBy: string | null
	confirmedAt: string | null
	createdBy: string | null
	isActive: boolean
	createdAt: string
	updatedAt: string
	deletedAt: string | null
}

export interface ImportMessagesResult {
	created: number
	messageIds: string[]
}

export type PackageType = 'work' | 'distribution'

/**
 * A typed download package, as the manifest endpoint answers it: the files that belong to it,
 * each described well enough to be presented before anything is downloaded.
 *
 * Same shape as a `DeliverableFile` on the detail aggregate — both are a discovered file the
 * server has already presigned a thumbnail for — so the same adapters read either.
 */
export interface PackageManifest {
	type: string
	files: DeliverableFile[]
}

/** Body for POST /work-orders/{id}/validation-asset-requests. */
export interface ValidationAssetRequestInput {
	assetType: string
	channel: string
}

/** One produced output in a `ready` render's manifest — carries its own display `label`. */
export interface ValidationAssetOutput {
	fileId: string
	kind: string
	label: string
}

/**
 * The produced-file references a `ready` render writes back. Each output carries a
 * template-owned `label` (e.g. "IG Mock", "PDF") the panel renders on its download
 * button. Legacy rows may instead carry the flat `fileIds` array; the panel reads
 * both defensively.
 */
export interface ValidationAssetResultManifest {
	outputs?: ValidationAssetOutput[]
	/** Legacy pre-labels shape — read defensively, never written by the current worker. */
	fileIds?: string[]
}

/** What an order's artwork is generated from, as stored (dates as ISO strings). */
export interface GenerationContext {
	workOrderId: string
	tenantId: number
	promptId: string
	/** The immutable version that rendered — what makes the generation reproducible. */
	promptVersionId: string
	values: Record<string, unknown>
	renderedText: string
	negativePrompt: string | null
	updatedBy: string | null
	createdAt: string
	updatedAt: string
}

export interface GenerationContextInput {
	promptId: string
	promptVersionId: string
	values: Record<string, unknown>
	renderedText: string
	negativePrompt?: string
}

/** The `validation_asset_request` row returned by the request/list endpoints (dates as ISO strings). */
export interface ValidationAssetRequest {
	requestId: string
	tenantId: number
	workOrderId: string
	assetType: string
	channel: string
	status: ValidationAssetStatus
	version: number
	resultManifest: ValidationAssetResultManifest | null
	error: string | null
	requestedBy: string | null
	isActive: boolean
	createdAt: string
	updatedAt: string
	deletedAt: string | null
}
